import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { IconeCheque } from '@ui/shell/icones'
import type { Previa } from './resumo-da-confirmacao'

/**
 * O resumo antes de gravar — decisão 305.
 *
 * ## Dois toques, e o segundo é informado
 *
 * Confirmar grava preços no histórico e não tem volta. O primeiro toque abre
 * isto, e só o "Confirmar" daqui grava. A escolha foi do dono, entre isto e o
 * mesmo botão com um rótulo mais honesto: quem decide vê exatamente o que vai
 * virar permanente.
 *
 * ## O que não está ok tem a cor de pendência, e só ela
 *
 * Amarelo é "falta alguém decidir" (`DESIGN.md`, a Regra do Trabalho Único), e
 * é o que uma sugestão não aceita ou um valor sem categoria são. O que está ok
 * leva o visto em texto apagado — confirmação, não festa.
 *
 * ## Com bloqueio, não há "Confirmar"
 *
 * Um abatimento maior que a categoria seria recusado pela Function depois de a
 * nota já estar confirmada. O botão não aparece em vez de aparecer
 * desabilitado: não há nada que a pessoa faça AQUI para habilitá-lo — o
 * conserto é na linha, e a frase do bloqueio diz isso.
 */
export function ResumoDaConfirmacao({
  previa,
  onConfirmar,
  onVoltar,
  onIrAte,
}: {
  previa: Previa
  onConfirmar: () => void
  onVoltar: () => void
  /** Fecha a folha e leva até a linha que um item cita. Decisão 310. */
  onIrAte: (linhaId: string) => void
}) {
  return (
    <FolhaDeBaixo titulo="Confirmar a nota" onFechar={onVoltar}>
      <div className="flex flex-col gap-3 p-1">
        <div>
          <h2 className="titulo text-lg">Confirmar a nota</h2>
          {/* Só o que é verdade em toda nota. O que vai para o histórico é a
              lista abaixo que diz — o subtítulo dizia "os preços dela ficam
              no histórico" sempre, e contradizia a lista numa nota sem data
              (decisão 307). */}
          <p className="mt-0.5 text-xs text-[var(--text-muted)]">
            Depois de confirmada, a nota não muda mais.
          </p>
        </div>

        {previa.itens.length > 0 && (
          <ul className="flex flex-col gap-2" aria-label="O que vai ser gravado">
            {previa.itens.map((item) => (
              <li key={item.texto} className="flex items-start gap-2 text-sm">
                {item.tom === 'ok' ? (
                  <span className="mt-px shrink-0 text-[var(--text-muted)] [&>svg]:size-4">
                    <IconeCheque />
                  </span>
                ) : (
                  <span
                    aria-hidden="true"
                    className="mt-1.5 size-1.5 shrink-0 rounded-full"
                    style={{ background: 'var(--pendente)' }}
                  />
                )}
                <span>
                  {item.texto}
                  {item.linhaId !== undefined && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="font-medium text-[var(--brand)] underline underline-offset-2"
                        onClick={() => {
                          if (item.linhaId !== undefined) onIrAte(item.linhaId)
                        }}
                      >
                        Ir até ela
                      </button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {previa.bloqueio !== null && (
          <p role="alert" className="text-sm" style={{ color: 'var(--pendente)' }}>
            {previa.bloqueio}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {previa.bloqueio === null && (
            <Botao tom="primario" type="button" onClick={onConfirmar}>
              Confirmar
            </Botao>
          )}
          {/* O foco abre AQUI, e não no "Confirmar": gravar exige um gesto
              deliberado, e não o segundo Enter de quem abriu a folha. */}
          <button
            type="button"
            data-foco-inicial
            onClick={onVoltar}
            className="alvo px-3 text-sm text-[var(--text-muted)]"
          >
            Voltar
          </button>
        </div>
      </div>
    </FolhaDeBaixo>
  )
}
